import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { HiChevronDown, HiChevronUp } from "react-icons/hi2";
import { FaBus, FaStar, FaSnowflake, FaChair } from "react-icons/fa";
import { baseurl } from "../../baseurl";
import "../../asset/css/bus-results.css";
import { BiArrowToRight } from "react-icons/bi";

const BusResult = () => {
  const [searchParams] = useSearchParams();
  const [trips, setTrips] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  // --- Markup State ---
  const [busMarkup, setBusMarkup] = useState({ type: "flat", amount: 0 });

  // Filter States
  const [priceRange, setPriceRange] = useState(5000);
  const [selectedBusTypes, setSelectedBusTypes] = useState([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]); 
  const [selectedTravels, setSelectedTravels] = useState([]);

  // --- MARKUP LOGIC FUNCTION ---
  // const calculateFinalFare = useCallback((baseFare, tripName = "Trip") => {
  //   const fare = parseFloat(baseFare) || 0;
  //   const markupAmt = parseFloat(busMarkup.amount) || 0;
  //   let finalFare = 0;

  //   if (busMarkup.type === "percentage") {
  //     finalFare = fare + (fare * (markupAmt / 100));
  //     console.log(`[Fare Calc] ${tripName}: Base(${fare}) + ${markupAmt}% Markup = ${finalFare}`);
  //   } else {
  //     // Default to 'flat'
  //     finalFare = fare + markupAmt;
  //     console.log(`[Fare Calc] ${tripName}: Base(${fare}) + ${markupAmt} Flat Markup = ${finalFare}`);
  //   }
    
  //   return Math.round(finalFare);
  // }, [busMarkup]);

  // --- TIME FORMATTER LOGIC ---
  const formatBusTime = (totalMinutes) => {
    if (totalMinutes === undefined || totalMinutes === null) return { timeString: "--", dayString: "" };
    const totalHours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    const journeyDay = Math.floor(totalHours / 24);
    const hour24 = totalHours % 24;
    const period = hour24 >= 12 ? "P.M." : "A.M.";
    const hour12 = hour24 % 12 || 12; 
    return {
      timeString: `${hour12}:${mins.toString().padStart(2, '0')} ${period}`,
      dayString: journeyDay > 0 ? `(+${journeyDay} Day)` : ""
    };
  };

// --- MARKUP LOGIC FUNCTION ---
  const calculateFinalFare = useCallback((baseFare, tripName = "Trip") => {
    const fare = parseFloat(baseFare) || 0;
    const markupAmt = parseFloat(busMarkup.amount) || 0;
    const type = busMarkup.type?.toLowerCase(); // Handle "percent" or "flat"
    
    let finalFare = 0;
    let calculationLog = "";

    if (type === "percent" || type === "percentage") {
      const addedAmount = fare * (markupAmt / 100);
      finalFare = fare + addedAmount;
      calculationLog = `Base: ${fare} + (${markupAmt}% of ${fare} = ${addedAmount})`;
    } else {
      // Default to 'flat'
      finalFare = fare + markupAmt;
      calculationLog = `Base: ${fare} + Flat Markup: ${markupAmt}`;
    }

    const roundedFare = Math.round(finalFare);

    // Detailed Console Log for Debugging
    // console.group(`Fare Calculation: ${tripName}`);
    // console.log(`Calculation: ${calculationLog}`);
    // console.log(`Final Rounded Result: ₹${roundedFare}`);
    // console.groupEnd();
    
    return roundedFare;
  }, [busMarkup]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Markup Data
        const markupRes = await axios.post(`${baseurl}/getServiceMarkupsGuest`);
        
        // Find service_id 6 (Bus)
        const busService = markupRes.data.markups?.find(m => Number(m.service_id) === 6);
        
        if (busService) {
          setBusMarkup({
            type: busService.markup_type, // "percent" or "flat"
            amount: busService.amount
          });
          console.log(`%c Markup Loaded: ${busService.amount} (${busService.markup_type})`, "color: green; font-weight: bold;");
        }

        // 2. Fetch Trips
        const payload = {
          source: searchParams.get("sourceId"),
          destination: searchParams.get("destId"),
          doj: searchParams.get("date")?.split('.').reverse().join('-'),
        };
        const response = await axios.post(`${baseurl}/getAvailableTrips`, payload);
        const available = response.data.trips?.availableTrips || [];

        const sortedTrips = available.sort((a, b) => parseInt(a.departureTime) - parseInt(b.departureTime));

        setTrips(sortedTrips);
        setFilteredTrips(sortedTrips);
      } catch (err) {
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (searchParams.get("sourceId")) fetchData();
  }, [searchParams]);




  useEffect(() => {
    let result = [...trips];

    // 1. Price Filter (Applying markup BEFORE checking the range)
    result = result.filter(t => {
        const baseFare = t.fareDetails?.[0]?.totalFare || t.fares || 0;
        const displayedFare = calculateFinalFare(baseFare);
        return displayedFare <= priceRange;
    });

    // 2. Bus Type Filter
    if (selectedBusTypes.length > 0) {
      result = result.filter(t => selectedBusTypes.some(type => {
        if (type === 'AC') return String(t.AC) === "true";
        if (type === 'Non AC') return String(t.AC) === "false";
        if (type === 'Sleeper') return String(t.sleeper) === "true" || t.busType.toLowerCase().includes('sleeper');
        if (type === 'Seater') return String(t.seater) === "true" || t.busType.toLowerCase().includes('seater');
        return false;
      }));
    }

    // 3. Time Slot Filter
    if (selectedTimeSlots.length > 0) {
      result = result.filter(t => {
        const depTime = parseInt(t.departureTime);
        return selectedTimeSlots.some(slot => {
          if (slot === 'morning') return depTime >= 0 && depTime < 600;
          if (slot === 'afternoon') return depTime >= 600 && depTime <= 1020;
          if (slot === 'evening') return depTime > 1020 && depTime <= 1380;
          if (slot === 'night') return depTime > 1380;
          return false;
        });
      });
    }

    // 4. Travels Filter
    if (selectedTravels.length > 0) {
      result = result.filter(t => selectedTravels.includes(t.travels));
    }

    setFilteredTrips(result);
  }, [priceRange, selectedBusTypes, selectedTimeSlots, selectedTravels, trips, calculateFinalFare]);

  const handleToggle = (state, setState, value) => {
    setState(prev => prev.includes(value) ? prev.filter(i => i !== value) : [...prev, value]);
  };

  const distinctTravels = useMemo(() => [...new Set(trips.map(t => t.travels))].sort(), [trips]);

  if (loading) return <div className="text-center p-5 mt-5"><FaBus className="fa-spin h1 text-danger"/><h3>Loading Buses...</h3></div>;

  return (
    <section className="bus-results-bg py-4 bg-light min-vh-100 mt-5 pt-5">
      <div className="container">
        <div className="row g-3">
          {/* Sidebar Filters */}
          <div className="col-lg-3 d-none d-lg-block">
            <div className="bg-white rounded-3 shadow-sm p-3 sticky-top" style={{top: '100px'}}>


<style>
  {`
  .route-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f9fa; /* Light subtle background */
  padding: 8px 16px;
  border-radius: 8px;
  width: fit-content;
  border: 1px solid #e0e0e0;
  margin-bottom: 15px;
}

.location-text {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  text-transform: capitalize;
}

.route-icon {
  color: #3498db; /* Blue accent for the arrow */
  font-size: 1.2rem;
  display: flex;
  align-items: center;
}

/* Optional: Make it look better on mobile */
@media (max-width: 480px) {
  .route-indicator {
    gap: 8px;
    padding: 6px 12px;
  }
  .location-text {
    font-size: 0.9rem;
  }
}
`}
</style>

              <div className="route-indicator">
  <span className="location-text">{searchParams.get("sourceName")}</span>
  <BiArrowToRight className="route-icon" />
  <span className="location-text">{searchParams.get("destName")}</span>
</div>


              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold m-0">Filters</h6>
                <button className="btn btn-sm text-danger p-0" onClick={() => {
                  setPriceRange(5000); setSelectedBusTypes([]); setSelectedTimeSlots([]); setSelectedTravels([]);
                }}>Reset</button>
              </div>

              <div className="mb-4">
                <label className="small fw-bold text-muted mb-2 d-block">BUS TYPE</label>
                <div className="row g-2">
                  {['AC', 'Non AC', 'Sleeper', 'Seater'].map(type => (
                    <div className="col-6" key={type}>
                      <button 
                        className={`btn btn-sm w-100 border ${selectedBusTypes.includes(type) ? 'btn-danger' : 'btn-outline-secondary'}`}
                        onClick={() => handleToggle(selectedBusTypes, setSelectedBusTypes, type)}
                      >{type}</button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="small fw-bold text-muted mb-2 d-block">PRICE RANGE (₹{priceRange})</label>
                <input type="range" className="form-range" min="300" max="5000" step="50"
                  value={priceRange} onChange={(e) => setPriceRange(e.target.value)} />
              </div>

              <div className="mb-4">
                <label className="small fw-bold text-muted mb-2 d-block">DEPARTURE TIME</label>
                <div className="row g-2">
                  {[
                    {id: 'morning', label: 'Before 10AM'},
                    {id: 'afternoon', label: '10AM - 5PM'},
                    {id: 'evening', label: '5PM - 11PM'},
                    {id: 'night', label: 'After 11PM'}
                  ].map(slot => (
                    <div className="col-6" key={slot.id}>
                      <button 
                        className={`btn btn-sm w-100 border ${selectedTimeSlots.includes(slot.id) ? 'btn-danger' : 'btn-outline-secondary'}`}
                        onClick={() => handleToggle(selectedTimeSlots, setSelectedTimeSlots, slot.id)}
                        style={{fontSize: '10px'}}
                      >{slot.label}</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Area */}
          <div className="col-lg-9">






            <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
              <h6 className="fw-bold small mb-3">Popular Operators</h6>
              <div className="d-flex gap-2 flex-wrap">
                {distinctTravels.slice(0, 8).map(name => (
                  <span 
                    key={name}
                    className={`badge border p-2 px-3 fw-normal cursor-pointer text-truncate ${selectedTravels.includes(name) ? 'bg-danger text-white' : 'bg-light text-dark'}`}
                    style={{ maxWidth: '120px' }}
                    onClick={() => handleToggle(selectedTravels, setSelectedTravels, name)}
                  >{name}</span>
                ))}
              </div>
            </div>

            <div className="mb-3 d-flex justify-content-between align-items-center">
                <span className="fw-bold">{filteredTrips.length} Buses found</span>
                <span className="small text-muted">Sorted by: Departure Time</span>
            </div>

            {filteredTrips.map((trip) => {
              // Calculate Markup Fare
              const baseFare = trip.fareDetails?.[0]?.totalFare || trip.fares;
              const finalFare = calculateFinalFare(baseFare, trip.travels);
              
              const departure = formatBusTime(trip.departureTime);
              const arrival = formatBusTime(trip.arrivalTime);

              return (
                <div className="bus-card-abhibus bg-white shadow-sm rounded-3 mb-3 border overflow-hidden" key={trip.id}>
                  <div className="row g-0 p-3 align-items-center">
                    <div className="col-md-3">
                      <h6 className="fw-bold mb-1 text-truncate">{trip.travels}</h6>
                      <p className="text-muted x-small mb-1">{trip.busType}</p>
                      <span className="badge bg-success small"><FaStar size={10}/> 4.2</span>
                    </div>

                    <div className="col-md-6 px-lg-4 text-center">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="h5 fw-bold mb-0 text-danger">{departure.timeString}</div>
                          <div className="x-small text-muted text-uppercase">{searchParams.get("sourceName")}</div>
                        </div>
                        <div className="flex-grow-1 px-3">
                          <div className="x-small text-muted mb-1">{trip.duration}</div>
                          <div className="border-top w-100 position-relative">
                             <div className="position-absolute translate-middle-y top-50 start-0 bg-secondary rounded-circle" style={{width:6, height:6}}></div>
                             <div className="position-absolute translate-middle-y top-50 end-0 bg-danger rounded-circle" style={{width:6, height:6}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="h5 fw-bold mb-0">
                            {arrival.timeString} 
                            {arrival.dayString && <small className="d-block text-danger" style={{fontSize: '9px'}}>{arrival.dayString}</small>}
                          </div>
                          <div className="x-small text-muted text-uppercase">{searchParams.get("destName")}</div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3 text-md-end border-md-start ps-md-4">
                      {/* Using the final mark-up fare here */}
                      <div className="text-muted x-small text-decoration-line-through">₹{finalFare + 120}</div>
                      <div className="h4 fw-bold mb-0">₹{finalFare}</div>
                      <p className="text-danger x-small fw-bold mb-2">{trip.availableSeats} Seats Left</p>
                     <Link 
  to={
    localStorage.getItem("guestUserId") 
      ? `/bus-seats/${trip.id}/${searchParams.get("sourceId")}/${searchParams.get("destId")}` 
      : "/login"
  }
  className="btn btn-danger btn-sm w-100 fw-bold"
>
  Select Seats
</Link>
                    </div>
                  </div>

                  <div className="bg-light p-2 border-top px-3 d-flex justify-content-between align-items-center">
                    <div className="x-small text-primary fw-bold cursor-pointer" onClick={() => setExpandedId(expandedId === trip.id ? null : trip.id)}>
                      Details {expandedId === trip.id ? <HiChevronUp/> : <HiChevronDown/>}
                    </div>
                    <div className="x-small text-muted">
                        {String(trip.AC) === "true" ? <><FaSnowflake/> AC</> : "Non-AC"} 
                        <FaChair className="ms-2"/> {trip.busType.toLowerCase().includes("sleeper") ? "Sleeper" : "Seater"}
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredTrips.length === 0 && (
                <div className="text-center py-5 bg-white rounded shadow-sm">
                    <img src="https://cdni.iconscout.com/illustration/premium/thumb/no-bus-available-8628701-6884144.png" alt="No Bus" style={{width: '200px'}} />
                    <h5 className="mt-3 text-muted">No buses found for this filter.</h5>
                    <button className="btn btn-danger btn-sm mt-2" onClick={() => setPriceRange(5000)}>Clear Filters</button>
                </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusResult;